import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { SharedTextInput, SharedButton } from "../../../shared";
import { useCreateBulletinPostMutation } from "../../../../store/slices/api";
import { selectUser } from "../../../../store/slices/authentication";
import { useSelector } from "react-redux";
import FileUploader from "./FileUploader";
import { CATEGORIES, categoryLabel } from "../categories";
import { failedMessage } from "../../../shared/serverReason";

const MAX_TITLE_LENGTH = 100;
const MAX_CONTENT_LENGTH = 5000;
const MAX_PHONE_NUMBER_LENGTH = 20;
const MAX_EMAIL_LENGTH = 100;
const MAX_LOCATION_LENGTH = 100;

const POST_FAILED =
  "Your post didn't go through. Everything you wrote is still here — please try again.";

function CreatePost() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  // True while a photo or file is still on its way up, so the post can't go
  // out without it.
  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [createBulletinPost, { isLoading }] = useCreateBulletinPostMutation();

  const shouldDisableCreatePost = () => {
    return (
      title.length === 0 || content.length === 0 || !category || isUploading
    );
  };

  const handleSubmit = async () => {
    if (isLoading || shouldDisableCreatePost()) return;
    setSubmitError(null);
    // Only leave the form once the post is really saved. On failure every
    // field and upload stays as it was, and the member is told why.
    try {
      await createBulletinPost({
        individual_id: parseInt(user.externalId),
        title: title,
        content: content,
        category: category,
        attachments: attachments,
        phone_number: phoneNumber,
        email: email,
        location: location,
      }).unwrap();
    } catch (err) {
      setSubmitError(failedMessage(err, POST_FAILED));
      return;
    }
    navigate("/bulletin?tab=posts");
  };

  return (
    <div className="flex flex-col rounded-[16px] shadow-xl dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-[32px] border border-dupontGold bg-white dark:bg-darkCard gap-y-[16px]">
      <div className="text-[18px] font-bold text-dupontBlue dark:text-darkText">
        Create a Bulletin Post
      </div>
      <SharedTextInput
        label="Title*"
        name="title"
        value={title}
        onChange={setTitle}
        variant="outlined"
        maxChars={MAX_TITLE_LENGTH}
        helperText="The headline cousins see first."
      />
      <Autocomplete
        options={CATEGORIES}
        value={category}
        onChange={(_, newValue) => setCategory(newValue)}
        getOptionLabel={(option) => categoryLabel(option)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Category*"
            variant="outlined"
            helperText="Which umbrella does your post fall under?"
          />
        )}
        size="small"
      />
      <SharedTextInput
        label="Post Content*"
        name="content"
        value={content}
        onChange={setContent}
        multiline
        rows={4}
        variant="outlined"
        maxChars={MAX_CONTENT_LENGTH}
        helperText="Details and description. Need more space? Attach a file below."
      />
      <FileUploader
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        onUploadingChange={setIsUploading}
      />
      <SharedTextInput
        label="Phone Number (optional)"
        name="phoneNumber"
        value={phoneNumber}
        onChange={setPhoneNumber}
        variant="outlined"
        maxChars={MAX_PHONE_NUMBER_LENGTH}
      />
      <SharedTextInput
        label="Email"
        name="email"
        value={email}
        onChange={setEmail}
        variant="outlined"
        maxChars={MAX_EMAIL_LENGTH}
      />
      <SharedTextInput
        label="Location (optional)"
        name="location"
        value={location}
        onChange={setLocation}
        variant="outlined"
        maxChars={MAX_LOCATION_LENGTH}
      />
      {submitError && (
        <div role="alert" className="text-[15px] font-body text-red-700 dark:text-red-400">
          {submitError}
        </div>
      )}
      <SharedButton
        disabled={shouldDisableCreatePost()}
        onClick={handleSubmit}
        loadingText={isLoading ? "Creating post..." : undefined}
      >
        Create Post
      </SharedButton>
    </div>
  );
}

export default CreatePost;
